import React, { useState } from "react";
import { X, Download, Loader2, CheckCircle2 } from "lucide-react";
import { listThemes, generateAnnualBook } from "../lib/pdfEngine";
import { CHAPTERS } from "../data/content";

const YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 6 }, (_, i) => YEAR - i);

export default function AnnualBookModal({ open, onClose, stories, profile, family }) {
  const [themeId, setThemeId] = useState("classic");
  const [year, setYear] = useState(YEAR);
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // { url, filename }
  const [error, setError] = useState("");

  if (!open) return null;

  const generate = async () => {
    setBusy(true); setError(""); setProgress(0); setDone(null);
    try {
      // Filter to selected year (fallback: all stories if none in year)
      const filtered = stories.filter((s) => {
        const d = s.updated_at || s.created_at;
        if (!d) return false;
        return new Date(d).getFullYear() === year;
      });
      const use = filtered.length ? filtered : stories;
      const chapters = [1, 2, 3, 4, 5, 6, 7].map((id) => CHAPTERS[id - 1]);
      const res = await generateAnnualBook({
        stories: use,
        chapters,
        meta: {
          year,
          familyName: family?.name || "Our Family",
          storytellerName: profile?.name || "Our Storyteller",
        },
        themeId,
        onProgress: setProgress,
      });
      setDone(res);
    } catch (e) {
      setError("Something went wrong preparing your book. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 overflow-y-auto"
      style={{ background: "rgba(17,29,51,0.82)" }}
      data-testid="annual-book-modal"
      onClick={onClose}>
      <div className="max-w-2xl w-full p-8 sm:p-10 relative bg-[#FAF7F2]"
        style={{ borderRadius: 4, border: "1px solid #B8973A" }}
        onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-[#8C8278] hover:text-[#111D33]" data-testid="annual-book-close">
          <X size={20} />
        </button>
        <div className="text-center mb-6">
          <p className="label-ui text-[11px] text-[#B8973A] tracking-[0.25em]">Annual Book</p>
          <h2 className="text-3xl sm:text-4xl mt-3" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 300 }}>
            Download <span style={{ fontStyle: "italic" }}>your Living Book</span>
          </h2>
          <p className="text-[#8C8278] italic mt-3" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.1rem" }}>
            A beautifully typeset PDF of the stories captured this year.
          </p>
        </div>

        {/* Theme picker */}
        <div className="mb-6">
          <p className="label-ui text-[10px] text-[#8C8278] mb-3">Choose a layout theme</p>
          <div className="grid grid-cols-3 gap-3">
            {listThemes().map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setThemeId(t.id)}
                className={`p-4 text-center transition-all ${themeId === t.id ? "bg-white" : "bg-[#F7F0E0] hover:bg-white"}`}
                style={{ borderRadius: 4, border: themeId === t.id ? "1px solid #B8973A" : "1px solid #F0EAE0" }}
                data-testid={`theme-${t.id}`}
              >
                <p className="label-ui text-[10px] text-[#B8973A] mb-1">Theme</p>
                <p className="text-lg" style={{ fontFamily: t.id === "modern" ? "system-ui, sans-serif" : t.id === "scrapbook" ? "'Courier New', monospace" : "Cormorant Garamond, serif" }}>
                  {t.label}
                </p>
              </button>
            ))}
          </div>
        </div>

        {/* Year picker */}
        <div className="mb-6">
          <p className="label-ui text-[10px] text-[#8C8278] mb-2">Year</p>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))}
            className="w-full px-4 py-3 hairline-cream bg-white"
            style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "1rem" }}
            data-testid="annual-book-year">
            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>

        {/* Progress or download */}
        {busy && (
          <div className="mb-4" data-testid="annual-book-progress">
            <div className="w-full h-1.5 bg-[#F0EAE0] rounded-full overflow-hidden">
              <div className="h-full bg-[#B8973A] transition-all" style={{ width: `${Math.round(progress * 100)}%` }} />
            </div>
            <p className="text-center text-[#8C8278] italic mt-3" style={{ fontFamily: "Cormorant Garamond, serif" }}>
              Typesetting your book · {Math.round(progress * 100)}%
            </p>
          </div>
        )}

        {done && !busy && (
          <div className="mb-4 text-center" data-testid="annual-book-done">
            <CheckCircle2 size={28} className="mx-auto text-[#B8973A]" />
            <p className="text-[#111D33] italic mt-3" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.15rem" }}>
              Your book is ready.
            </p>
          </div>
        )}

        {error && <p className="text-red-700 text-sm text-center mb-4" data-testid="annual-book-error">{error}</p>}

        {!done ? (
          <button onClick={generate} disabled={busy}
            className="btn-primary w-full flex items-center justify-center gap-2"
            data-testid="annual-book-generate">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
            {busy ? "Preparing…" : "Generate PDF"}
          </button>
        ) : (
          <a href={done.url} download={done.filename}
            className="btn-primary w-full flex items-center justify-center gap-2"
            data-testid="annual-book-download">
            <Download size={16} /> Download {done.filename}
          </a>
        )}

        <p className="text-center text-xs text-[#8C8278] italic mt-4" style={{ fontFamily: "Cormorant Garamond, serif" }}>
          The transcription text remains editable in your Living Book at any time.
        </p>
      </div>
    </div>
  );
}
